// ═══════════════════════════════════════════════════════════════
// ContactReportForm.jsx
// Path: src/components/donorDetail/ContactReportForm.jsx
//
// The donor contact report capture form.
//   - 6 required fields always visible
//   - 9 optional intelligence sections, all collapsed by default
//   - Pin-to-donor-record checkboxes are intentionally absent in v1 Phase 3;
//     they land in Phase 4 alongside the sidebar Donor Notes panel.
//
// Phase 3 of CRM_Donor_Contact_Reports_Blueprint_v1_0.md
// ═══════════════════════════════════════════════════════════════

import React, { useState, useMemo } from "react";
import { ChevronDown, Save, X } from "lucide-react";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { FormInput, FormSelect } from "../ui/FormControls";
import { ActionButton } from "../ui/ActionButton";
import {
  MEETING_TYPES,
  OPTIONAL_SECTIONS,
  REQUIRED_FIELDS,
} from "../../constants/contactReportSections";

// ── Date helpers ──────────────────────────────────────────────
const todayStr = () => new Date().toISOString().slice(0, 10);
const inDaysStr = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// ── Default empty form ────────────────────────────────────────
const buildEmptyForm = () => ({
  visitDate: todayStr(),
  meetingType: "",
  attendees: "",
  summary: "",
  nextStep: "",
  nextStepDate: inDaysStr(14),
  // intelligence sub-fields stored flat for easier form binding,
  // then assembled into the nested shape on submit
  family: "",
  occupation: "",
  recreation: "",
  messagingEmphasize: "",
  messagingAvoid: "",
  wealth_signals: "",
  philanthropic: "",
  network: "",
  capacity_update: "",
  strategic_note: "",
});

// ── Build form state from an existing report (edit mode) ──────
// Inverse of buildIntelligence — flattens nested intelligence into the
// flat form shape so existing values can populate the textareas.
const buildFormFromReport = (report) => {
  const intel = report.intelligence || {};
  return {
    visitDate: report.visitDate || todayStr(),
    meetingType: report.meetingType || "",
    attendees: report.attendees || "",
    summary: report.summary || "",
    nextStep: report.nextStep || "",
    nextStepDate: report.nextStepDate || inDaysStr(14),
    family: intel.family?.notes || "",
    occupation: intel.occupation?.notes || "",
    recreation: intel.recreation?.notes || "",
    messagingEmphasize: intel.messaging?.emphasize || "",
    messagingAvoid: intel.messaging?.avoid || "",
    wealth_signals: intel.wealth_signals?.notes || "",
    philanthropic: intel.philanthropic?.notes || "",
    network: intel.network?.notes || "",
    capacity_update: intel.capacity_update?.notes || "",
    strategic_note: intel.strategic_note?.notes || "",
  };
};

// ── Build the initial expanded-section state for edit mode ────
// Sections with existing content auto-expand so the user can see and
// edit them without having to click into each one.
const buildInitialExpandedFromReport = (report) => {
  if (!report) return {};
  const intel = report.intelligence || {};
  const expanded = {};
  if (intel.family?.notes) expanded.family = true;
  if (intel.occupation?.notes) expanded.occupation = true;
  if (intel.recreation?.notes) expanded.recreation = true;
  if (intel.messaging?.emphasize || intel.messaging?.avoid) {
    expanded.messaging = true;
  }
  if (intel.wealth_signals?.notes) expanded.wealth_signals = true;
  if (intel.philanthropic?.notes) expanded.philanthropic = true;
  if (intel.network?.notes) expanded.network = true;
  if (intel.capacity_update?.notes) expanded.capacity_update = true;
  if (intel.strategic_note?.notes) expanded.strategic_note = true;
  return expanded;
};

// ── Assemble flat form values into the nested intelligence object ──
const buildIntelligence = (form) => {
  const intel = {};
  if (form.family && form.family.trim()) intel.family = { notes: form.family.trim() };
  if (form.occupation && form.occupation.trim()) intel.occupation = { notes: form.occupation.trim() };
  if (form.recreation && form.recreation.trim()) intel.recreation = { notes: form.recreation.trim() };

  const emphasize = (form.messagingEmphasize || "").trim();
  const avoid = (form.messagingAvoid || "").trim();
  if (emphasize || avoid) {
    intel.messaging = {};
    if (emphasize) intel.messaging.emphasize = emphasize;
    if (avoid) intel.messaging.avoid = avoid;
  }

  if (form.wealth_signals && form.wealth_signals.trim())
    intel.wealth_signals = { notes: form.wealth_signals.trim() };
  if (form.philanthropic && form.philanthropic.trim())
    intel.philanthropic = { notes: form.philanthropic.trim() };
  if (form.network && form.network.trim())
    intel.network = { notes: form.network.trim() };
  if (form.capacity_update && form.capacity_update.trim())
    intel.capacity_update = { notes: form.capacity_update.trim() };
  if (form.strategic_note && form.strategic_note.trim())
    intel.strategic_note = { notes: form.strategic_note.trim() };

  return intel;
};

// ── Inline textarea — matches the existing DonorDetailMain pattern ──
const Textarea = ({ value, onChange, placeholder, rows = 3, palette }) => (
  <textarea
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    rows={rows}
    placeholder={placeholder}
    style={{
      width: "100%",
      padding: "10px 12px",
      borderRadius: 8,
      fontSize: FONT_SIZES.md,
      fontFamily: FONT_FAMILY,
      outline: "none",
      background: palette.inputBg,
      color: palette.inputColor,
      border: `1px solid ${palette.inputBorder}`,
      resize: "vertical",
      boxSizing: "border-box",
    }}
  />
);

// ── Collapsible optional section header ──────────────────────
const SectionToggle = ({ label, expanded, onToggle, palette, hasContent }) => (
  <button
    type="button"
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
      padding: "10px 14px",
      marginBottom: expanded ? 8 : 6,
      borderRadius: 8,
      border: `1px solid ${hasContent ? palette.accent1 : palette.cardBorder}`,
      background: hasContent ? `${palette.accent1}10` : palette.inputBg,
      color: palette.text1,
      fontSize: FONT_SIZES.sm,
      fontWeight: 600,
      fontFamily: FONT_FAMILY,
      cursor: "pointer",
      transition: "all 0.15s",
    }}
  >
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {hasContent && (
        <span
          style={{
            display: "inline-block",
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: palette.accent1,
          }}
        />
      )}
      {label}
    </span>
    <ChevronDown
      size={ICON_SIZES.sm}
      style={{
        transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
        transition: "transform 0.2s",
      }}
    />
  </button>
);

// ═══════════════════════════════════════════════════════════════
// ContactReportForm
// ═══════════════════════════════════════════════════════════════

export const ContactReportForm = ({
  palette,
  onSave,
  onCancel,
  saving,
  initialReport,
}) => {
  const isEditMode = !!initialReport;

  const [form, setForm] = useState(() =>
    initialReport ? buildFormFromReport(initialReport) : buildEmptyForm()
  );
  const [expanded, setExpanded] = useState(() =>
    buildInitialExpandedFromReport(initialReport)
  );
  // Pin checkboxes are intentionally hidden in edit mode (v1) — pin
  // management is separate from edits to avoid accidentally overwriting
  // donor sidebar pins on a routine typo fix.
  const [pins, setPins] = useState({
    family: false,
    occupation: false,
    recreation: false,
    messagingEmphasize: false,
    messagingAvoid: false,
  });
  const [showErrors, setShowErrors] = useState(false);

  const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const toggleSection = (key) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  const togglePin = (pinKey) =>
    setPins((prev) => ({ ...prev, [pinKey]: !prev[pinKey] }));

  // Required-field validation
  const missing = useMemo(() => {
    const m = [];
    REQUIRED_FIELDS.forEach((field) => {
      const value = form[field];
      if (!value || (typeof value === "string" && !value.trim())) m.push(field);
    });
    return m;
  }, [form]);

  const isValid = missing.length === 0;

  const handleSubmit = () => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    // Strip pin flags for sections with no content (can't pin nothing)
    const finalPins = {
      family: pins.family && !!form.family.trim(),
      occupation: pins.occupation && !!form.occupation.trim(),
      recreation: pins.recreation && !!form.recreation.trim(),
      messagingEmphasize:
        pins.messagingEmphasize && !!form.messagingEmphasize.trim(),
      messagingAvoid: pins.messagingAvoid && !!form.messagingAvoid.trim(),
    };
    onSave(
      {
        visitDate: form.visitDate,
        meetingType: form.meetingType,
        attendees: form.attendees.trim(),
        summary: form.summary.trim(),
        nextStep: form.nextStep.trim(),
        nextStepDate: form.nextStepDate,
        intelligence: buildIntelligence(form),
      },
      finalPins
    );
  };

  // Inline pin checkbox row — used at the bottom of each pinnable section
  const PinCheckbox = ({ pinKey, hasContent }) => (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 8,
        fontSize: FONT_SIZES.xs,
        fontWeight: 600,
        color: hasContent ? palette.text2 : palette.text3,
        cursor: hasContent ? "pointer" : "not-allowed",
        fontFamily: FONT_FAMILY,
        userSelect: "none",
      }}
    >
      <input
        type="checkbox"
        checked={!!pins[pinKey] && hasContent}
        disabled={!hasContent}
        onChange={() => togglePin(pinKey)}
        style={{ cursor: hasContent ? "pointer" : "not-allowed" }}
      />
      📌 Pin to donor record
      {!hasContent && (
        <span style={{ color: palette.text3, fontWeight: 400 }}>
          (add text to pin)
        </span>
      )}
    </label>
  );

  const errorStyle = (field) =>
    showErrors && missing.includes(field)
      ? { boxShadow: "0 0 0 2px #ef444440", borderRadius: 8 }
      : {};

  // Detect content per optional section for the dot indicator
  const sectionHasContent = (section) => {
    if (section.split) {
      return (
        (form.messagingEmphasize && form.messagingEmphasize.trim().length > 0) ||
        (form.messagingAvoid && form.messagingAvoid.trim().length > 0)
      );
    }
    const val = form[section.key];
    return val && val.trim().length > 0;
  };

  return (
    <div
      style={{
        background: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: 12,
        padding: "20px 24px",
      }}
    >
      {/* ── Required Fields ─────────────────────────────────── */}
      <h3
        style={{
          margin: "0 0 16px",
          fontSize: FONT_SIZES.md,
          fontWeight: 700,
          color: palette.text1,
          fontFamily: FONT_FAMILY,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {isEditMode ? "Edit Contact Report" : "New Contact Report"}
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 14,
          marginBottom: 14,
        }}
      >
        <div style={errorStyle("visitDate")}>
          <FormInput
            label="Visit Date"
            type="date"
            value={form.visitDate}
            onChange={(v) => update("visitDate", v)}
            required
          />
        </div>
        <div style={errorStyle("meetingType")}>
          <FormSelect
            label="Meeting Type *"
            value={form.meetingType}
            onChange={(v) => update("meetingType", v)}
            options={MEETING_TYPES}
            placeholder="Select..."
          />
        </div>
      </div>

      <div style={{ marginBottom: 14, ...errorStyle("attendees") }}>
        <FormInput
          label="Who Was Present"
          value={form.attendees}
          onChange={(v) => update("attendees", v)}
          placeholder="e.g., Donor + spouse + me + ED"
          required
        />
      </div>

      <div style={{ marginBottom: 14, ...errorStyle("summary") }}>
        <label
          style={{
            fontSize: FONT_SIZES.sm,
            fontWeight: 600,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            display: "block",
            marginBottom: 4,
          }}
        >
          Summary <span style={{ color: "#ef4444" }}>*</span>
        </label>
        <Textarea
          value={form.summary}
          onChange={(v) => update("summary", v)}
          placeholder="Narrative of the conversation — what was discussed, how they responded, key moments"
          rows={5}
          palette={palette}
        />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <div style={errorStyle("nextStep")}>
          <FormInput
            label="Next Step"
            value={form.nextStep}
            onChange={(v) => update("nextStep", v)}
            placeholder="e.g., Send program brief and schedule follow-up call"
            required
          />
        </div>
        <div style={errorStyle("nextStepDate")}>
          <FormInput
            label="Next-Step Due"
            type="date"
            value={form.nextStepDate}
            onChange={(v) => update("nextStepDate", v)}
            required
          />
        </div>
      </div>

      {showErrors && !isValid && (
        <div
          style={{
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 8,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontSize: FONT_SIZES.sm,
            fontFamily: FONT_FAMILY,
          }}
        >
          Please fill in all required fields before saving.
        </div>
      )}

      {/* ── Optional Sections ───────────────────────────────── */}
      <div
        style={{
          marginTop: 8,
          paddingTop: 16,
          borderTop: `1px dashed ${palette.cardBorder}`,
        }}
      >
        <p
          style={{
            margin: "0 0 12px",
            fontSize: FONT_SIZES.xs,
            fontWeight: 600,
            color: palette.text2,
            fontFamily: FONT_FAMILY,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          Intelligence (optional — expand only the sections that apply)
        </p>

        {OPTIONAL_SECTIONS.map((section) => {
          const isExpanded = !!expanded[section.key];
          const hasContent = sectionHasContent(section);
          return (
            <div key={section.key}>
              <SectionToggle
                label={section.label}
                expanded={isExpanded}
                onToggle={() => toggleSection(section.key)}
                palette={palette}
                hasContent={hasContent}
              />
              {isExpanded && (
                <div
                  style={{
                    padding: "0 4px 14px 4px",
                    marginBottom: 8,
                  }}
                >
                  {section.split ? (
                    <>
                      {section.fields.map((subfield, idx) => {
                        const flatKey =
                          subfield.key === "emphasize"
                            ? "messagingEmphasize"
                            : "messagingAvoid";
                        const subHasContent =
                          form[flatKey] && form[flatKey].trim().length > 0;
                        return (
                          <div
                            key={subfield.key}
                            style={{ marginBottom: idx === 0 ? 16 : 0 }}
                          >
                            <label
                              style={{
                                display: "block",
                                fontSize: FONT_SIZES.xs,
                                fontWeight: 600,
                                color: palette.text2,
                                fontFamily: FONT_FAMILY,
                                marginBottom: 4,
                                textTransform: "uppercase",
                                letterSpacing: "0.05em",
                              }}
                            >
                              {subfield.label}
                            </label>
                            <Textarea
                              value={form[flatKey]}
                              onChange={(v) => update(flatKey, v)}
                              placeholder={subfield.placeholder}
                              rows={2}
                              palette={palette}
                            />
                            {subfield.pinnable && !isEditMode && (
                              <PinCheckbox
                                pinKey={flatKey}
                                hasContent={subHasContent}
                              />
                            )}
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <>
                      <Textarea
                        value={form[section.key]}
                        onChange={(v) => update(section.key, v)}
                        placeholder={section.placeholder}
                        rows={3}
                        palette={palette}
                      />
                      {section.pinnable && !isEditMode && (
                        <PinCheckbox
                          pinKey={section.key}
                          hasContent={
                            form[section.key] &&
                            form[section.key].trim().length > 0
                          }
                        />
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Action buttons ──────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 24,
          paddingTop: 16,
          borderTop: `1px solid ${palette.cardBorder}`,
        }}
      >
        <ActionButton
          label="Cancel"
          icon={X}
          variant="secondary"
          onClick={onCancel}
          disabled={saving}
        />
        <ActionButton
          label={
            saving
              ? "Saving..."
              : isEditMode
                ? "Save Changes"
                : "Save Contact Report"
          }
          icon={Save}
          onClick={handleSubmit}
          disabled={saving}
        />
      </div>
    </div>
  );
};
